import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';

export default function ServiceSelection({
    options,
    selectedServices,
    selectedBundleIds = [],
    toggleOption,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    categoryList,
    pastServiceIds = [],
    popularServiceIds = []
}) {
    const { t, i18n } = useTranslation();
    const debouncedQuery = useDebounce(searchQuery, 300);

    const getOptionDisplayName = (opt) => {
        return (i18n.language && i18n.language.startsWith('ar') && opt.name_ar) ? opt.name_ar : opt.name;
    };

    const isOptionSelected = (opt) => {
        if (opt.type === 'service') return selectedServices.includes(opt.service_ids[0]);
        return selectedBundleIds.includes(opt.id);
    };

    const pastSet = useMemo(() => new Set(pastServiceIds || []), [pastServiceIds]);
    const popularSet = useMemo(() => new Set(popularServiceIds || []), [popularServiceIds]);
    const isPreviouslyBooked = (opt) => (opt.service_ids || []).some(id => pastSet.has(id));
    const isMostPopular = (opt) => (opt.service_ids || []).some(id => popularSet.has(id));

    const filteredOptions = useMemo(() => {
        const filtered = (options || []).filter(opt => {
            const q = debouncedQuery.toLowerCase();
            const matchName = (opt.name || '').toLowerCase().includes(q) || (opt.name_ar || '').toLowerCase().includes(q);
            const matchCat = selectedCategory === 'All' || opt.category_name === selectedCategory;
            return matchName && matchCat;
        });
        return [...filtered].sort((a, b) => {
            const aPast = isPreviouslyBooked(a);
            const bPast = isPreviouslyBooked(b);
            if (aPast && !bPast) return -1;
            if (!aPast && bPast) return 1;
            const aPopular = isMostPopular(a);
            const bPopular = isMostPopular(b);
            if (aPopular && !bPopular) return -1;
            if (!aPopular && bPopular) return 1;
            return 0;
        });
    }, [options, debouncedQuery, selectedCategory, pastSet, popularSet]);

    const serviceBadgeChipStyle = {
        background: 'var(--orange)',
        color: '#ffffff',
        padding: '0.2rem 0.55rem',
        fontSize: '0.625rem',
        fontWeight: 700,
        borderRadius: '4px',
        letterSpacing: '0.02em',
    };

    return (
        <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: 'var(--orange)', border: '2px solid var(--orange)', borderRadius: '50%', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
                {t('booking.selectServices')}
            </h2>

            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <input
                    type="text"
                    placeholder={t('booking.searchServices')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: '#fff', fontSize: '0.9rem', width: '100%' }}
                />
                <select
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: '#fff', fontSize: '0.9rem', width: '100%' }}
                >
                    <option value="All">{t('booking.allCategories')}</option>
                    {categoryList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            {filteredOptions.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem', border: '1px dashed var(--border)', borderRadius: '8px' }}>
                    {t('booking.noServicesFound')}
                </p>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                    {filteredOptions.map(opt => {
                        const isSelected = isOptionSelected(opt);
                        const previouslyBooked = isPreviouslyBooked(opt);
                        const mostPopular = isMostPopular(opt);
                        const displayName = getOptionDisplayName(opt);
                        const key = opt.type === 'bundle' ? `bundle-${opt.id}` : `service-${opt.id}`;
                        return (
                            <div
                                key={key}
                                onClick={() => toggleOption(opt)}
                                style={{
                                    padding: '1.25rem',
                                    background: isSelected ? 'var(--card-focus-ring)' : 'var(--bg-card)',
                                    border: `1px solid ${isSelected ? 'var(--orange)' : 'var(--border)'}`,
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                {isSelected && (
                                    <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--orange)', color: '#ffffff', padding: '0.2rem 0.6rem', fontSize: '0.7rem', fontWeight: 700, borderBottomLeftRadius: '8px' }}>
                                        {t('booking.selected')}
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
                                    {previouslyBooked && (
                                        <span style={serviceBadgeChipStyle}>{t('booking.previouslyBooked')}</span>
                                    )}
                                    {mostPopular && (
                                        <span style={serviceBadgeChipStyle}>{t('booking.mostPopular')}</span>
                                    )}
                                    {opt.category_name && (
                                        <span style={{ fontSize: '0.65rem', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
                                            {opt.type === 'bundle' ? t('booking.bundle') : opt.category_name}
                                        </span>
                                    )}
                                </div>
                                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.4rem' }}>{displayName}</h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                                    <span style={{ color: '#ffffff' }}>⏱ {opt.duration_minutes} {t('booking.mins')}</span>
                                    <span style={{ color: '#ffffff', fontWeight: 700 }}>{opt.price} EGP</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '1.25rem' }}>
                {t('booking.selectAllServicesPolicy')}
            </p>
        </div>
    );
}
